import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  SchedulerCalendarEventType,
  SchedulerAgentMeetPage,
  SchedulerCalendarEvent,
  SchedulerCalendarEventAdd,
  SchedulerCalendarPublicEvent,
} from 'sb-lib/types';
import { RejectError } from 'shared/api/axios';
import {
  createSchedulerCalendarEventType,
  updateSchedulerCalendarEventType,
  deleteSchedulerCalendarEventType,
  patchSchedulerAgentMeetPage,
  postPublicSchedulerEvent,
  deleteSchedulerCalendarEvent,
  patchSchedulerCalendarEvent,
  updatePublicSchedulerEvent,
  cancelPublicSchedulerEvent,
} from 'shared/api';
import type { AppState } from './reducers';
import { FilterId, FilterItemType } from 'browser/components/Scheduling/shared';

// THUNKS

export const addSchedulerCalendarEventType = createAsyncThunk<
  SchedulerCalendarEventType,
  Partial<SchedulerCalendarEventType>,
  { state: AppState; rejectValue: RejectError }
>('scheduler/addSchedulerCalendarEventType', async data => {
  return (await createSchedulerCalendarEventType(data)).data;
});

export const editSchedulerCalendarEventType = createAsyncThunk<
  SchedulerCalendarEventType,
  Partial<SchedulerCalendarEventType>,
  { state: AppState; rejectValue: RejectError }
>('scheduler/editSchedulerCalendarEventType', async data => {
  return (await updateSchedulerCalendarEventType(data)).data;
});

export const removeSchedulerCalendarEventType = createAsyncThunk<
  void,
  number,
  { state: AppState; rejectValue: RejectError }
>('scheduler/removeSchedulerCalendarEventType', async id => {
  await deleteSchedulerCalendarEventType(id);
});

export const editSchedulerAgentMeetPage = createAsyncThunk<
  SchedulerAgentMeetPage,
  SchedulerAgentMeetPage,
  { state: AppState; rejectValue: RejectError }
>('scheduler/editSchedulerAgentMeetPage', async data => {
  return (await patchSchedulerAgentMeetPage(data)).data;
});

export const addSchedulerEventPublic = createAsyncThunk<
  SchedulerCalendarPublicEvent,
  SchedulerCalendarEventAdd,
  { state: AppState; rejectValue: RejectError }
>('scheduler/addSchedulerEventPublic', async data => {
  return (await postPublicSchedulerEvent(data)).data;
});

export const changeSchedulerEventPublic = createAsyncThunk<
  SchedulerCalendarPublicEvent,
  { id: string; startTime: string },
  { state: AppState; rejectValue: RejectError }
>('scheduler/changeSchedulerEventPublic', async data => {
  return (await updatePublicSchedulerEvent(data.id, { startTime: data.startTime })).data;
});

export const cancelSchedulerEventPublic = createAsyncThunk<
  SchedulerCalendarPublicEvent,
  { id: string },
  { state: AppState; rejectValue: RejectError }
>('scheduler/cancelSchedulerEvent', async data => {
  return (await cancelPublicSchedulerEvent(data.id)).data;
});

export const editSchedulerMeeting = createAsyncThunk<
  SchedulerCalendarEvent,
  { id: number; startTime: Date },
  { state: AppState; rejectValue: RejectError }
>('scheduler/editSchedulerMeeting', async ({ id, startTime }) => {
  return (await patchSchedulerCalendarEvent(id, startTime)).data;
});

export const cancelSchedulerMeeting = createAsyncThunk<
  SchedulerCalendarEvent,
  { id: number; message: string },
  { state: AppState; rejectValue: RejectError }
>('scheduler/cancelSchedulerMeeting', async ({ id, message }) => {
  return (await deleteSchedulerCalendarEvent(id, message)).data;
});

export const initialState = {
  activeFilter: {
    id: 'me',
    type: FilterItemType.USER,
  } as FilterId,
};

// SLICE

const schedulerSlice = createSlice({
  name: 'scheduler',
  initialState,
  reducers: {
    setActiveFilter(state, action: PayloadAction<FilterId>) {
      state.activeFilter = action.payload;
    },
    resetActiveFilter(state) {
      state.activeFilter = initialState.activeFilter;
    },
  },
});

// SELECTORS

export const selectActiveFilter = (state: AppState) => state.scheduler.activeFilter;

export const { setActiveFilter, resetActiveFilter } = schedulerSlice.actions;

export default schedulerSlice.reducer;
